import { CommonModule } from '@angular/common';
import { LocalizationModule, LocalizationService, SubscriptionService } from '@abp/ng.core';
import { ConfirmationService } from '@abp/ng.theme.shared';
import { ColDef, ValueGetterParams } from '@ag-grid-community/core';
import { Component, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AgGridComponent,
  AuditedColumns,
  BaseDapperListPage,
  DwGridDataSourceService,
  FieldTypes,
  IEnumerationCellRendererParams,
  ListPageComponent,
} from '@digiwall/ag-grid';
import {
  InventoryCountDocListDto,
  InventoryCountDocService,
  InventoryCountStatus,
} from '@digiwall/goliath-documents/proxy';
import { GoliathDocumentEntityNames } from '../../../constants/entity-names.constants';
import { GoliathDocumentsEnumerationTypeStore } from '../../../enumeration-types';

@Component({
  selector: 'document-inventory-count-doc-list',
  templateUrl: './inventory-count-doc-list.component.html',
  styles: `
    :host {
      flex-grow: 1;
    }
  `,
  providers: [DwGridDataSourceService],
  imports: [CommonModule, ListPageComponent, AgGridComponent, LocalizationModule],
})
export class InventoryCountDocListComponent extends BaseDapperListPage<
  InventoryCountDocListDto,
  InventoryCountDocService
> {
  protected enumerationTypeStoreDoc = inject(GoliathDocumentsEnumerationTypeStore);
  protected GoliathDocumentEntityNames: any = GoliathDocumentEntityNames;

  /** Deletion is only allowed while every selected inventory count is still in Draft. */
  protected override get enableDelete(): boolean {
    const selected = this.selectedEntities();
    return selected.length > 0 && selected.every(x => x.status === InventoryCountStatus.Draft);
  }

  override colDefs: ColDef[] = [
    ...AuditedColumns(this.localizationService),
    {
      field: 'companySite',
      headerName: this.localizationService.instant('::CompanySite'),
      type: FieldTypes.String,
    },
    {
      field: 'status',
      headerName: this.localizationService.instant('Doc::InventoryCount:Status'),
      type: FieldTypes.Enumeration,
      cellRendererParams: {
        entities: this.enumerationTypeStoreDoc.inventoryCountStatus,
      } as IEnumerationCellRendererParams,
    },
    {
      field: 'inventoryDate',
      headerName: this.localizationService.instant('Doc::InventoryCount:Date'),
      type: FieldTypes.Date,
    },
    {
      field: 'responsible',
      headerName: this.localizationService.instant('::Responsible'),
      type: FieldTypes.String,
    },
    {
      field: 'storageLocations',
      headerName: this.localizationService.instant('Doc::InventoryCount:StorageLocations'),
      type: FieldTypes.String,
    },
    {
      field: 'priority',
      headerName: this.localizationService.instant('Doc::InventoryCount:Priority'),
      type: FieldTypes.Number,
    },
    {
      colId: 'assignedOperator',
      headerName: this.localizationService.instant('Doc::InventoryCount:AssignedOperator'),
      type: FieldTypes.String,
      valueGetter: (params: ValueGetterParams<InventoryCountDocListDto>) =>
        [params.data?.workerFirstName, params.data?.workerLastName].filter(Boolean).join(' '),
    },
    {
      field: 'description',
      headerName: this.localizationService.instant('::Description'),
      type: FieldTypes.String,
      hide: true,
    },
    {
      field: 'note',
      headerName: this.localizationService.instant('::Note'),
      type: FieldTypes.String,
      hide: true,
    },
  ];

  constructor(
    protected service: InventoryCountDocService,
    protected router: Router,
    protected route: ActivatedRoute,
    protected localizationService: LocalizationService,
    protected confirmation: ConfirmationService,
    protected subscriptionService: SubscriptionService,
    protected datasource: DwGridDataSourceService,
  ) {
    super(service, route, router, confirmation, subscriptionService, datasource);
  }
}
